import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, Button, Icon, SkeletonText, Tooltip } from '@chakra-ui/react';
import { AddressRec } from '@netiva/classifieds-api';
import { DescriptionList, DescriptionListItem } from '@netiva/classifieds-ui';
import { FiEye } from 'react-icons/fi';

import { Country, Item } from './components';

export type AddressProps = BoxProps & {
  address?: AddressRec;
  onEdit: VoidFunction;
};

const ShowPhoneIcon = () => {
  const { t } = useTranslation();
  return (
    <Tooltip label={t('address.form.showPhone')}>
      <Box display="inline-block" ml={2}>
        <Icon as={FiEye} boxSize={5} verticalAlign="center" />
      </Box>
    </Tooltip>
  );
};

export const Address: FunctionComponent<AddressProps> = ({ address, onEdit, ...rest }) => {
  const { t } = useTranslation();

  if (!address) {
    return (
      <Box {...rest}>
        <SkeletonText width={{ base: 'full', md: '500px' }} noOfLines={5} skeletonHeight={6} />
      </Box>
    );
  }

  return (
    <Box width={{ base: 'full', md: '500px' }} {...rest}>
      <DescriptionList width="full" flexShrink={1}>
        <Item label={t('address.form.name')} value={[address.firstname, address.lastname]} />
        <Item label={t('address.form.company')} value={address.company} />
        <Item label={t('address.form.company2')} value={address.company2} />
        <Item label={t('address.form.street')} value={[address.street, address.streetNo]} />
        <Item label={t('address.form.street2')} value={address.street2} />
        <Item label={t('address.form.city')} value={[address.zip, address.city]} />
        <Country label={t('address.form.country')} value={address.countryIso2} />
        <Item label={t('address.form.email')} value={address.email} />
        <Item
          label={t('address.form.phone')}
          value={address.showPhone ? [address.phone, <ShowPhoneIcon key="showPhone" />] : address.phone}
        />
        <Item
          label={t('address.form.mobile')}
          value={address.showMobile ? [address.mobile, <ShowPhoneIcon key="showPhone" />] : address.mobile}
        />
        <Item label={t('address.form.homepageUrl')} value={address.homepageUrl} />
        <DescriptionListItem display="flex">
          <Button onClick={onEdit} size="sm" colorScheme="neutral" marginLeft="auto">
            {t('ui.buttons.edit')}
          </Button>
        </DescriptionListItem>
      </DescriptionList>
    </Box>
  );
};
